import React from 'react';
import PropTypes from 'prop-types';

import { Pill, SkeletonBlock } from '../../../../styles/dunning/base';
import {
   Board,
   Column,
   ColumnBody,
   ColumnCount,
   ColumnDot,
   ColumnEmpty,
   ColumnFootBar,
   ColumnFootNote,
   ColumnHead,
   ColumnMetaRow,
   ColumnName,
   ColumnTitleRow,
   ColumnTotal,
   STAGE_TYPE_COLORS,
} from '../../../../styles/dunning/kanban';
import {
   STAGE_TYPE_LABELS,
   STAGE_TYPE_PILL_VARIANTS,
} from '../constants';
import { formatBRLShort } from '../../utils/dunningFormat';
import { caseFigures } from '../utils/derive';
import KanbanCard from './KanbanCard';

// ---------------------------------------------------------------------------
// Board do kanban: colunas por raia ativa (ordem de `position`), agrupamento
// por `stage.id` feito aqui no cliente sobre a MESMA resposta da fila.
//
// Bloqueios de drop (guia §5.1, revisto pelo 0103 §3.2):
// - TERMINAL_PAID nunca é alvo: sem realce e o container recusa com toast;
// - EXTERNAL também deixou de ser alvo — nenhum card entra nela por arraste.
//   Título vai para o escritório um a um, pelo fluxo "Enviar ao escritório"
//   (antes o drop aqui abria o modal que escalava o caso INTEIRO);
// - o realce só acontece com um drag em andamento.
// ---------------------------------------------------------------------------

// Raias que nunca recebem card arrastado — ver comentário do cabeçalho.
const UNDROPPABLE_STAGE_TYPES = new Set(['TERMINAL_PAID', 'EXTERNAL']);

function columnEmptyText(stage, statusFilter) {
   if (stage.stage_type === 'TERMINAL_PAID' && statusFilter === 'OPEN') {
      return 'Baixa automática pelo Asaas. Use o filtro Recebidos para ver os casos baixados.';
   }
   if (stage.stage_type === 'EXTERNAL') {
      return 'Os títulos chegam aqui pela ação "Enviar ao escritório", dentro do caso.';
   }
   return 'Nenhum caso nesta raia.';
}

export default function KanbanBoard({
   stages,
   rows,
   today,
   statusFilter,
   loading,
   hasMorePages,
   dragCaseId = null,
   dropStageId = null,
   onDragStart,
   onDragEnd,
   onDragOverStage,
   onDragLeaveStage,
   onDropOnStage,
   onOpenCase,
}) {
   if (loading) {
      return (
         <Board aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
               <Column key={i}>
                  <ColumnHead>
                     <SkeletonBlock $w="60%" $h="16px" />
                  </ColumnHead>
                  <ColumnBody>
                     <SkeletonBlock $h="96px" $radius="12px" />
                     <SkeletonBlock $h="96px" $radius="12px" />
                     <SkeletonBlock $h="96px" $radius="12px" />
                  </ColumnBody>
                  <ColumnFootBar />
               </Column>
            ))}
         </Board>
      );
   }

   return (
      <Board>
         {stages.map((stage) => {
            const cards = rows.filter((row) => row.stage?.id === stage.id);
            // Soma pelo bloco de cada caso (0102): na aba Recebidos o total da
            // coluna é o recebido, não o "em aberto" que é zero por definição.
            const figuresByCard = cards.map(caseFigures);
            const total = figuresByCard.reduce(
               (acc, figures) => acc + figures.amount,
               0
            );
            const chargeCount = figuresByCard.reduce(
               (acc, figures) => acc + figures.count,
               0
            );
            const countsLabel = `${cards.length} ${
               cards.length === 1 ? 'caso' : 'casos'
            } | ${chargeCount} ${chargeCount === 1 ? 'boleto' : 'boletos'}`;
            const isDropTarget =
               dragCaseId !== null &&
               !UNDROPPABLE_STAGE_TYPES.has(stage.stage_type);

            return (
               <Column
                  key={stage.id}
                  $dropActive={isDropTarget && dropStageId === stage.id}
                  onDragOver={(event) => {
                     event.preventDefault();
                     if (isDropTarget) onDragOverStage(stage.id);
                  }}
                  onDragLeave={() => onDragLeaveStage(stage.id)}
                  onDrop={(event) => {
                     event.preventDefault();
                     onDropOnStage(stage);
                  }}
               >
                  <ColumnHead>
                     {/* Posições trocadas (decisão 2026-08-14): o badge do
                         topo carrega o TOTAL em R$; a contagem vai por
                         extenso na linha de baixo, fora do badge. */}
                     <ColumnTitleRow>
                        <ColumnDot
                           $color={STAGE_TYPE_COLORS[stage.stage_type]}
                        />
                        <ColumnName title={stage.name}>{stage.name}</ColumnName>
                        <ColumnCount>{formatBRLShort(total)}</ColumnCount>
                     </ColumnTitleRow>
                     <ColumnMetaRow>
                        <Pill
                           $variant={
                              STAGE_TYPE_PILL_VARIANTS[stage.stage_type] ??
                              'neutral'
                           }
                        >
                           {STAGE_TYPE_LABELS[stage.stage_type] ?? '—'}
                        </Pill>
                        <ColumnTotal>{countsLabel}</ColumnTotal>
                     </ColumnMetaRow>
                  </ColumnHead>

                  <ColumnBody>
                     {cards.map((row) => (
                        <KanbanCard
                           key={row.id}
                           caseRow={row}
                           today={today}
                           isDragging={dragCaseId === row.id}
                           onOpen={() => onOpenCase(row.id)}
                           onDragStart={onDragStart}
                           onDragEnd={onDragEnd}
                        />
                     ))}
                     {cards.length === 0 && (
                        <ColumnEmpty>
                           {columnEmptyText(stage, statusFilter)}
                        </ColumnEmpty>
                     )}
                  </ColumnBody>

                  {hasMorePages && (
                     <ColumnFootNote>
                        Há mais casos além dos exibidos — refine a busca ou os
                        filtros.
                     </ColumnFootNote>
                  )}

                  {/* Sempre por último: fecha o canto arredondado por cima da
                      lista rolável e dá respiro ao último card. */}
                  <ColumnFootBar />
               </Column>
            );
         })}
      </Board>
   );
}

KanbanBoard.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   stages: PropTypes.array.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   rows: PropTypes.array.isRequired,
   today: PropTypes.string.isRequired,
   statusFilter: PropTypes.string.isRequired,
   loading: PropTypes.bool.isRequired,
   hasMorePages: PropTypes.bool.isRequired,
   dragCaseId: PropTypes.number,
   dropStageId: PropTypes.number,
   onDragStart: PropTypes.func.isRequired,
   onDragEnd: PropTypes.func.isRequired,
   onDragOverStage: PropTypes.func.isRequired,
   onDragLeaveStage: PropTypes.func.isRequired,
   onDropOnStage: PropTypes.func.isRequired,
   onOpenCase: PropTypes.func.isRequired,
};
